import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { AnalyticsService } from 'services';
import { Document2Icon } from 'assets/icons';
import { DateRange } from '../hooks/use-date-range';
import { buildDateCategoryAxis } from '../utils/chart-axis';
import { buildGradientFill } from '../utils/chart-style';
import { buildRadialGaugeOptions } from '../utils/radial-gauge';
import SectionHeader from '../components/section-header';
import ChartCard from '../components/chart-card';
import KpiCard from '../components/kpi-card';

interface Props {
  range: DateRange;
}

const PreparationSection = ({ range }: Props) => {
  const [loadingSnapshot, setLoadingSnapshot] = useState(true);
  const [totals, setTotals] = useState({ total: 0, withLawyerSelected: 0 });

  const [loadingHistory, setLoadingHistory] = useState(true);
  const [history, setHistory] = useState<any[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingSnapshot(true);
      try {
        const res: any = await AnalyticsService.preparation.totals({ withoutLoading: true });
        if (!cancelled) setTotals(res || { total: 0, withLawyerSelected: 0 });
      } catch (error) {
        console.error('[PreparationSection] Error cargando totales', error);
      } finally {
        if (!cancelled) setLoadingSnapshot(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingHistory(true);
      try {
        const res: any = await AnalyticsService.preparation.creationHistory({
          ...range,
          withoutLoading: true,
        });
        if (!cancelled) setHistory(res || []);
      } catch (error) {
        console.error('[PreparationSection] Error cargando histórico de solicitudes', error);
      } finally {
        if (!cancelled) setLoadingHistory(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [range.startDate, range.endDate]);

  const categories = history.map((r) => moment(r.date).format('DD/MM'));
  const withLawyerRate =
    totals.total > 0 ? Math.round((totals.withLawyerSelected / totals.total) * 100) : 0;

  return (
    <section className='analytics-section'>
      <SectionHeader
        title='Elaboración documental'
        subtitle='El % con abogado no depende del filtro; todo caso pasa por la búsqueda de abogado, con o sin abogado conseguido'
      />
      <div className='grid grid-asym'>
        <ChartCard
          title='Solicitudes por día'
          loading={loadingHistory}
          empty={!loadingHistory && history.length === 0}
          type='area'
          height={380}
          series={[{ name: 'Solicitudes', data: history.map((r) => r.count) }]}
          options={{
            chart: { toolbar: { show: false }, zoom: { enabled: false } },
            dataLabels: { enabled: false },
            stroke: { curve: 'smooth', width: 3 },
            xaxis: buildDateCategoryAxis(categories),
            ...buildGradientFill(['#ef6526']),
          }}
        />
        <div className='side-stack'>
          <KpiCard label='Casos totales' value={totals.total} icon={Document2Icon} accent='blue' />
          <ChartCard
            title='% con abogado'
            loading={loadingSnapshot}
            empty={false}
            type='radialBar'
            height={180}
            series={[withLawyerRate]}
            options={buildRadialGaugeOptions('#ef6526', 'Con abogado')}
          />
        </div>
      </div>
    </section>
  );
};

export default PreparationSection;
